import React, { useState, useEffect, useRef } from 'react';
import { User } from '@/api/entities';
import { useUser, useCurrentTenant } from '@/contexts/UserContext';
import { resolveEffectiveTenant } from '@/lib/tenantUtils';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle2, XCircle, Mail, Loader2, AlertCircle, ExternalLink, RefreshCw, Save } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { useTranslation } from '@/components/utils/translations';

const getApiBaseUrl = () => {
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'https://api.neogtm.com';
  }
  return 'https://api.neogtm.com';
};

const API_BASE_URL = getApiBaseUrl();

export default function SettingsOutlook({ currentTenant: currentTenantProp }) {
  const { t } = useTranslation();
  const contextUser = useUser();
  const contextTenant = useCurrentTenant();
  const currentTenant = currentTenantProp || contextTenant;
  const tenantId = resolveEffectiveTenant(
    contextUser?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null,
    currentTenant,
    contextUser,
    'instructure'
  );
  const user = contextUser;
  const [isLoading, setIsLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [isLoadingAliases, setIsLoadingAliases] = useState(false);
  const [isSavingSignature, setIsSavingSignature] = useState(false);
  const [outlookConnected, setOutlookConnected] = useState(false);
  const [outlookEmail, setOutlookEmail] = useState(null);
  const [displayName, setDisplayName] = useState(null);
  const [aliases, setAliases] = useState([]);
  const [selectedAlias, setSelectedAlias] = useState('');
  const [signatureHtml, setSignatureHtml] = useState('');
  const [savedSignature, setSavedSignature] = useState('');
  const [error, setError] = useState(null);
  const [apiAvailable, setApiAvailable] = useState(true);
  const signatureRef = useRef(null);
  const { toast } = useToast();
  const oauthListenerRef = useRef(null);
  const oauthTimeoutRef = useRef(null);
  const oauthPollRef = useRef(null);

  useEffect(() => {
    loadUserAndStatus();
  }, [contextUser]);

  useEffect(() => {
    return () => {
      if (oauthListenerRef.current) {
        window.removeEventListener('message', oauthListenerRef.current);
        oauthListenerRef.current = null;
      }
      if (oauthTimeoutRef.current) clearTimeout(oauthTimeoutRef.current);
      if (oauthPollRef.current) clearInterval(oauthPollRef.current);
    };
  }, []);

  useEffect(() => {
    if (outlookConnected && apiAvailable) {
      loadAliases();
    }
  }, [outlookConnected, apiAvailable]);

  useEffect(() => {
    if (user && apiAvailable) {
      loadSavedSignature();
    }
  }, [user, apiAvailable]);

  const loadUserAndStatus = async () => {
    if (!contextUser) return;
    try {
      setIsLoading(true);
      setError(null);

      const userId = contextUser.user_id || contextUser.id || contextUser.email;

      if (!userId) {
        throw new Error('Unable to identify user');
      }

      try {
        const response = await fetch(
          `${API_BASE_URL}/api/outlook/status?tenant_id=${tenantId}&user_id=${userId}`,
          { credentials: 'include' }
        );

        if (response.ok) {
          const data = await response.json();
          setOutlookConnected(data.connected);
          setOutlookEmail(data.email);
          setDisplayName(data.display_name);
          setApiAvailable(true);

          if (contextUser.preferred_outlook_alias) {
            setSelectedAlias(contextUser.preferred_outlook_alias);
          }
        } else if (response.status === 404) {
          setApiAvailable(false);
          setOutlookConnected(false);
          setOutlookEmail(null);
        } else {
          setOutlookConnected(false);
          setOutlookEmail(null);
        }
      } catch (apiError) {
        console.log('Outlook API not available:', apiError.message);
        setApiAvailable(false);
        setOutlookConnected(false);
        setOutlookEmail(null);
      }
    } catch (err) {
      console.error('Error loading user:', err);
      setError('Failed to load user data');
    } finally {
      setIsLoading(false);
    }
  };

  const loadSavedSignature = async () => {
    if (!user) return;
    try {
      const userId = user.user_id || user.id || user.email;
      
      const response = await fetch(
        `${API_BASE_URL}/api/settings/email-signature?tenant_id=${tenantId}&user_id=${userId}`,
        { credentials: 'include' }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.email_signature_html) {
          setSavedSignature(data.email_signature_html);
          setSignatureHtml(data.email_signature_html);
        }
      }
    } catch (err) {
      console.error('Error loading saved signature:', err);
    }
  };

  const loadAliases = async () => {
    if (!apiAvailable || !user || !user.id) return;
    
    try {
      setIsLoadingAliases(true);
      
      const userId = user.user_id || user.id || user.email;
      const response = await fetch(
        `${API_BASE_URL}/api/outlook/aliases?tenant_id=${tenantId}&user_id=${userId}`,
        { credentials: 'include' }
      );

      if (!response.ok) {
        if (response.status === 404) {
          // Optional endpoint in some backend deployments.
          // Fall back to the connected mailbox without showing an error toast.
          console.warn('Outlook aliases endpoint unavailable (404); using connected mailbox fallback.');
          if (outlookEmail) {
            const fallback = [{ email: outlookEmail, displayName: displayName || null, isDefault: true }];
            setAliases(fallback);
            if (!selectedAlias) {
              setSelectedAlias(outlookEmail);
            }
          }
          return;
        }
        throw new Error('Failed to load email aliases');
      }

      const data = await response.json();
      if (data.success && data.aliases) {
        setAliases(data.aliases);
        
        if (!selectedAlias && data.default) {
          setSelectedAlias(data.default);
        }
      }
    } catch (err) {
      console.error('Error loading aliases:', err);
      toast({
        title: t('error'),
        description: t('failedToLoadEmailAddresses'),
        variant: 'destructive'
      });
    } finally {
      setIsLoadingAliases(false);
    }
  };

  const savePreferredAlias = async (email) => {
    try {
      await User.updateMyUserData({ preferred_outlook_alias: email });
      return true;
    } catch (err) {
      console.error('Error saving preferred alias:', err);
      return false;
    }
  };

  const handleAliasChange = async (email) => {
    setSelectedAlias(email);
    const persisted = await savePreferredAlias(email);
    if (persisted) {
      toast({ title: t('emailAddressUpdated'), description: `${t('emailsWillBeSentFrom')} ${email}` });
    } else {
      toast({
        title: t('emailAddressUpdatedLocalOnly'),
        description: `${t('usingInSessionBackendRejectedAlias')} ${email}`,
        variant: 'destructive'
      });
    }
  };

  const handleRefreshAliases = async () => {
    setAliases([]);
    await loadAliases();
    toast({ title: t('emailAddressesRefreshed'), description: t('emailAliasesReloaded') });
  };

  const handlePaste = (e) => {
    e.preventDefault();
    
    const html = e.clipboardData.getData('text/html');
    const text = e.clipboardData.getData('text/plain');
    
    if (html) {
      const cleanHtml = html
        .replace(/<!--[\s\S]*?-->/g, '')
        .replace(/<meta[^>]*>/gi, '')
        .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
      
      setSignatureHtml(cleanHtml);
      if (signatureRef.current) {
        signatureRef.current.innerHTML = cleanHtml;
      }
    } else if (text) {
      const htmlFromText = text.replace(/\n/g, '<br>');
      setSignatureHtml(htmlFromText);
      if (signatureRef.current) {
        signatureRef.current.innerHTML = htmlFromText;
      }
    }
  };

  const handleSignatureInput = () => {
    if (signatureRef.current) {
      setSignatureHtml(signatureRef.current.innerHTML);
    }
  };

  const handleSaveSignature = async () => {
    if (!user) return;
    
    try {
      setIsSavingSignature(true);
      
      const userId = user.user_id || user.id || user.email;
      
      const response = await fetch(
        `${API_BASE_URL}/api/settings/email-signature`,
        {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tenant_id: tenantId,
            user_id: userId,
            email_signature_html: signatureHtml,
          }),
        }
      );

      if (response.ok) {
        setSavedSignature(signatureHtml);
        toast({ 
          title: t('signatureSaved'), 
          description: t('emailSignatureSavedSuccessfully') 
        });
      } else {
        throw new Error('Failed to save signature');
      }
    } catch (err) {
      console.error('Error saving signature:', err);
      toast({
        title: t('error'),
        description: t('failedToSaveSignature'),
        variant: 'destructive'
      });
    } finally {
      setIsSavingSignature(false);
    }
  };

  const handleClearSignature = () => {
    setSignatureHtml('');
    if (signatureRef.current) {
      signatureRef.current.innerHTML = '';
    }
  };

  const clearOutlookOAuthState = () => {
    localStorage.removeItem('outlook_oauth_tenant_id');
    localStorage.removeItem('outlook_oauth_user_id');
  };

  const handleConnectOutlook = async () => {
    if (!apiAvailable) {
      setError('Outlook integration is not available yet.');
      return;
    }

    try {
      setIsConnecting(true);
      setError(null);

      const userId = user.user_id || user.id || user.email;

      if (!userId) throw new Error('User not loaded');

      localStorage.setItem('outlook_oauth_tenant_id', tenantId);
      localStorage.setItem('outlook_oauth_user_id', userId);

      const redirectUri = `${window.location.origin}/SettingsOutlookCallback`;
      const response = await fetch(
        `${API_BASE_URL}/api/outlook/oauth/start?tenant_id=${tenantId}&user_id=${encodeURIComponent(userId)}&redirect_uri=${encodeURIComponent(redirectUri)}`,
        { method: 'GET', credentials: 'include' }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to start Outlook authorization');
      }

      const data = await response.json();

      if (!data.authUrl) {
        throw new Error('No authorization URL received from server');
      }

      const width = 520;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        data.authUrl,
        'outlook_oauth',
        `width=${width},height=${height},left=${left},top=${top},popup=yes`
      );

      if (!popup || popup.closed) {
        window.location.href = data.authUrl;
        return;
      }

      const cleanup = () => {
        if (oauthListenerRef.current) {
          window.removeEventListener('message', oauthListenerRef.current);
          oauthListenerRef.current = null;
        }
        if (oauthTimeoutRef.current) {
          clearTimeout(oauthTimeoutRef.current);
          oauthTimeoutRef.current = null;
        }
        if (oauthPollRef.current) {
          clearInterval(oauthPollRef.current);
          oauthPollRef.current = null;
        }
      };

      const onMessage = (event) => {
        if (event.origin !== window.location.origin) return;

        const payload = event?.data;
        if (!payload || payload.type !== 'oauth:complete' || payload.provider !== 'outlook') return;

        cleanup();
        setIsConnecting(false);

        if (payload.success) {
          setOutlookConnected(true);
          setOutlookEmail(payload.email);
          setDisplayName(payload.displayName);
          toast({
            title: t('outlookConnected'),
            description: payload.email ? `${t('connectedAs')} ${payload.email}` : t('outlookConnectedSuccessfully'),
          });
          loadUserAndStatus();
        } else {
          clearOutlookOAuthState();
          setError(payload.error || 'Authorization failed');
        }
      };

      oauthListenerRef.current = onMessage;
      window.addEventListener('message', onMessage);

      oauthPollRef.current = setInterval(() => {
        if (!popup || popup.closed) {
          cleanup();
          clearOutlookOAuthState();
          setIsConnecting(false);
          setError('Authentication window was closed before completion.');
        }
      }, 500);

      oauthTimeoutRef.current = setTimeout(() => {
        cleanup();
        clearOutlookOAuthState();
        setIsConnecting(false);
        setError('Outlook sign-in timed out');
      }, 60000);
    } catch (err) {
      console.error('Error connecting Outlook:', err);
      clearOutlookOAuthState();
      setIsConnecting(false);
      setError(err.message || 'Failed to connect Outlook.');
    }
  };

  const handleDisconnectOutlook = async () => {
    if (!apiAvailable) return;

    try {
      setIsDisconnecting(true);
      setError(null);

      const userId = user.user_id || user.id || user.email;

      if (!userId) throw new Error('User not loaded');

      const response = await fetch(
        `${API_BASE_URL}/api/outlook/disconnect?tenant_id=${tenantId}&user_id=${userId}`,
        { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' } }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to disconnect Outlook');
      }

      // Verify backend state before showing success to avoid false-positive UI states.
      const statusResponse = await fetch(
        `${API_BASE_URL}/api/outlook/status?tenant_id=${tenantId}&user_id=${userId}`,
        { credentials: 'include' }
      );
      const statusData = statusResponse.ok ? await statusResponse.json() : null;
      const stillConnected = statusData?.connected === true;

      if (!stillConnected) {
        localStorage.removeItem('outlook_connected');
        localStorage.removeItem('outlook_email');

        setOutlookConnected(false);
        setOutlookEmail(null);
        setDisplayName(null);
        setAliases([]);
        setSelectedAlias('');

        toast({ title: t('outlookDisconnected'), description: t('outlookAccountDisconnected') });
      } else {
        setOutlookConnected(true);
        setOutlookEmail(statusData?.email || outlookEmail);
        setDisplayName(statusData?.display_name || displayName);
        toast({
          title: t('disconnectNotApplied'),
          description: t('backendStillReportsOutlookConnected'),
          variant: 'destructive'
        });
      }
    } catch (err) {
      console.error('Error disconnecting Outlook:', err);
      setError(err.message || 'Failed to disconnect Outlook.');
    } finally {
      setIsDisconnecting(false);
    }
  };

  const hasUnsavedChanges = signatureHtml !== savedSignature;

  if (!contextUser) {
    return (
      <Card className="border shadow-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-[#0078D4]" />
            {t('outlookIntegration')}
          </CardTitle>
          <CardDescription>{t('connectOutlookDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            <span className="ml-2 text-sm text-gray-500">{t('loading')}</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card className="border shadow-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-[#0078D4]" />
            {t('outlookIntegration')}
          </CardTitle>
          <CardDescription>{t('connectOutlookDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            <span className="ml-2 text-sm text-gray-500">{t('loadingOutlookStatus')}</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border shadow-md">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="w-5 h-5 text-[#0078D4]" />
          {t('outlookIntegration')}
        </CardTitle>
        <CardDescription>{t('connectOutlookDescription')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!apiAvailable && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{t('outlookComingSoon')}</AlertDescription>
          </Alert>
        )}

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
          <div className="flex items-center gap-3">
            {outlookConnected ? (
              <>
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                <div>
                  <p className="font-medium text-sm">{t('outlookConnected')}</p>
                  {outlookEmail && (
                    <p className="text-xs text-gray-500">
                      {displayName ? `${displayName} (${outlookEmail})` : outlookEmail}
                    </p>
                  )}
                </div>
              </>
            ) : (
              <>
                <XCircle className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="font-medium text-sm">{t('outlookNotConnected')}</p>
                  <p className="text-xs text-gray-500">{t('connectAccountToSendEmails')}</p>
                </div>
              </>
            )}
          </div>

          {outlookConnected ? (
            <Button onClick={handleDisconnectOutlook} disabled={isDisconnecting || !apiAvailable} variant="outline" size="sm">
              {isDisconnecting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('disconnecting')}</>) : t('disconnect')}
            </Button>
          ) : (
            <Button onClick={handleConnectOutlook} disabled={isConnecting || !apiAvailable} size="sm" className="bg-[#0078D4] hover:bg-[#106EBE]">
              {isConnecting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('connecting')}</>) : (<><ExternalLink className="w-4 h-4 mr-2" />{t('connectOutlook')}</>)}
            </Button>
          )}
        </div>

        {outlookConnected && apiAvailable && (
          <div className="space-y-4 p-4 border rounded-lg bg-white">
            {aliases.length > 0 && (
              <>
                <div className="flex items-center justify-between">
                  <Label className="font-semibold">{t('sendEmailsFrom')}</Label>
                  <Button onClick={handleRefreshAliases} disabled={isLoadingAliases} variant="ghost" size="sm">
                    <RefreshCw className={`w-4 h-4 mr-2 ${isLoadingAliases ? 'animate-spin' : ''}`} />{t('refresh')}
                  </Button>
                </div>
                
                <Select value={selectedAlias} onValueChange={handleAliasChange} disabled={isLoadingAliases}>
                  <SelectTrigger><SelectValue placeholder={t('selectEmailAddress')} /></SelectTrigger>
                  <SelectContent>
                    {aliases.map((alias) => (
                      <SelectItem key={alias.email} value={alias.email}>
                        {alias.displayName ? `${alias.displayName} <${alias.email}>` : alias.email}
                        {alias.isDefault && ` (${t('default')})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </>
            )}

            <div className="pt-4 border-t">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <Label className="font-semibold">{t('emailSignature')}</Label>
                  <p className="text-xs text-gray-500 mt-1">
                    {t('copySignatureAndPasteBelow')}
                  </p>
                </div>
                {hasUnsavedChanges && (
                  <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">{t('unsavedChanges')}</span>
                )}
              </div>

              <div className="space-y-3">
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                  <p className="text-xs text-blue-800">
                    <strong>{t('howToCopyOutlookSignature')}</strong><br />
                    1. {t('outlookSignatureStep1')}<br />
                    2. {t('outlookSignatureStep2')}<br />
                    3. {t('outlookSignatureStep3')}
                  </p>
                </div>

                <div
                  ref={signatureRef}
                  contentEditable
                  onPaste={handlePaste}
                  onInput={handleSignatureInput}
                  className="min-h-[120px] max-h-[300px] overflow-y-auto p-3 border rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-[#0078D4] focus:border-transparent text-sm"
                  style={{ lineHeight: '1.5' }}
                  dangerouslySetInnerHTML={{ __html: signatureHtml }}
                  data-placeholder={t('pasteSignatureHere')}
                />

                <style>{`
                  [contenteditable]:empty:before {
                    content: attr(data-placeholder);
                    color: #9ca3af;
                    pointer-events: none;
                  }
                `}</style>

                <div className="flex items-center gap-2">
                  <Button 
                    onClick={handleSaveSignature} 
                    disabled={isSavingSignature || !signatureHtml || !hasUnsavedChanges}
                    size="sm"
                    className="bg-[#0078D4] hover:bg-[#106EBE]"
                  >
                    {isSavingSignature ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('saving')}</>
                    ) : (
                      <><Save className="w-4 h-4 mr-2" />{t('saveSignature')}</>
                    )}
                  </Button>
                  
                  {signatureHtml && (
                    <Button 
                      onClick={handleClearSignature} 
                      variant="outline" 
                      size="sm"
                    >
                      {t('clear')}
                    </Button>
                  )}
                </div>

                {savedSignature && !hasUnsavedChanges && (
                  <div className="flex items-center gap-2 text-xs text-green-600">
                    <CheckCircle2 className="w-3 h-3" />
                    {t('signatureSavedWillBeUsed')}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {!outlookConnected && apiAvailable && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <p className="font-medium mb-2">{t('whyConnectOutlook')}</p>
              <ul className="text-sm space-y-1 ml-4 list-disc">
                <li>{t('gmailBenefitSendPersonalized')}</li>
                <li>{t('gmailBenefitTrackEngagement')}</li>
                <li>{t('gmailBenefitManageOutreach')}</li>
                <li>{t('outlookWorksWith365')}</li>
              </ul>
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}